import { ComponentPropsWithoutRef, KeyboardEvent, Ref, useState } from 'react'

import { AstraButton, AstraPopover, astraTwMerge } from '~/astra'
import { ButtonTag } from '~/components'
import { Location, Practitioner, PractitionerStatusDefinition, RecurringAssignmentType } from '~/storm'

import { allRecurringAssignmentRoomDays, allRecurringAssignmentStatusDays } from '../../shared'
import { LocationDefinitionList } from './Edit/LocationDefinitionList'
import { StatusDefinitionList } from './Edit/StatusDefinitionList'
import { WeekdayDescriptor, weekdayDescriptors } from './weekdays'

const ABBREVIATED_WEEKDAYS = ['M', 'T', 'O', 'T', 'F']

type OnWeekdayClick = (args: { weekday: WeekdayDescriptor; status?: PractitionerStatusDefinition | null; room?: Location | null }) => void
type ButtonTagProps = ComponentPropsWithoutRef<typeof ButtonTag>
type PreviewTagProps = Partial<Omit<ButtonTagProps, 'colorType'>> & {
    status?: PractitionerStatusDefinition | null
    room?: Location | null
}

const PreviewTag = ({ status, room, ...props }: PreviewTagProps) => (
    <div className="flex flex-col gap-0.5">
        {status && <ButtonTag className={'h-[20px]'} label={status.label} colorType={status.color} fillType="all" {...props} />}
        {room && <ButtonTag className={'h-[20px]'} label={room.room_code} colorType="room" fillType="all" {...props} />}
    </div>
)
const PreviewTagWithPopover = ({
    status,
    room,
    onWeekdayClick,
    weekday,
    practitioner,
}: PreviewTagProps & {
    status?: PractitionerStatusDefinition | null
    room?: Location | null
    onWeekdayClick: OnWeekdayClick
    weekday: WeekdayDescriptor
    practitioner?: Practitioner
}) => {
    const [isOpen, setIsOpen] = useState(false)

    const handleStatusClick = (status: PractitionerStatusDefinition | null) => {
        setIsOpen(false)
        onWeekdayClick({ weekday, status })
    }

    const handleRoomClick = (room: Location | null) => {
        setIsOpen(false)
        onWeekdayClick({ weekday, room })
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOpen(true)
        }
    }

    return (
        <AstraPopover
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            className="z-priority min-w-52"
            align="center"
            popoverContent={
                <div className="flex flex-col gap-2">
                    <StatusDefinitionList assignedStatuses={[status?.code].filter(Boolean)} onStatusClick={handleStatusClick} practitioner={practitioner} />
                    {status && <AstraButton fullWidth label="Fjern" variant="tertiary" onClick={() => handleStatusClick(null)} />}
                    <hr className="border-base" />
                    <LocationDefinitionList assignedRoomCodes={[room?.room_code].filter(Boolean)} onLocationClick={handleRoomClick} />
                    {room && <AstraButton fullWidth label="Fjern" variant="tertiary" onClick={() => handleRoomClick(null)} />}
                </div>
            }
        >
            <div
                className={astraTwMerge('h-full rounded-md hover:shadow-sm', isOpen ? 'shadow-sm' : '')}
                role="button"
                tabIndex={0}
                onClick={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
            >
                <PreviewTag status={status} room={room} />
            </div>
        </AstraPopover>
    )
}
type WeekPreviewProps = {
    assignment: RecurringAssignmentType
    weekNumber: number
    onWeekdayClick?: OnWeekdayClick
}

const WeekPreview = ({ assignment, weekNumber, onWeekdayClick }: WeekPreviewProps) => {
    const assignedDays = allRecurringAssignmentStatusDays(assignment)
    const assignedRooms = allRecurringAssignmentRoomDays(assignment)
    const isRelevantForWeek = weekNumber % assignment.repeat_every === assignment.start_on % assignment.repeat_every

    return weekdayDescriptors.map((day, i) => {
        const status = assignedDays[i]
        const room = assignedRooms[i]
        return (
            <div key={`${i}-${weekNumber}`} className="rounded-md border border-subtle">
                {onWeekdayClick ? (
                    <PreviewTagWithPopover status={status} room={room} onWeekdayClick={onWeekdayClick} weekday={day} practitioner={assignment.practitioner} />
                ) : (
                    <PreviewTag status={isRelevantForWeek ? status : undefined} room={isRelevantForWeek ? room : undefined} disableHover />
                )}
            </div>
        )
    })
}

type Props = {
    assignment: RecurringAssignmentType
    numberOfWeeks?: number
    magicGradient?: boolean
    onWeekdayClick?: OnWeekdayClick
    disableBorder?: boolean
    ref?: Ref<HTMLDivElement>
}

export const RecurringAssignmentPreview = ({ assignment, numberOfWeeks = 4, magicGradient = false, onWeekdayClick, disableBorder = false, ref }: Props) => (
    <div
        className={astraTwMerge(
            'min-w-[330px] items-center rounded-lg border border-base p-4 text-center',
            magicGradient ? 'bg-gradient-to-r from-[#FAF8FF] to-white' : '',
            disableBorder ? 'border-none' : ''
        )}
        ref={ref}
    >
        <div
            className="grid gap-x-[15px] gap-y-[12px]"
            style={{
                gridTemplateColumns: `repeat(${ABBREVIATED_WEEKDAYS.length}, minmax(35px, 1fr))`,
                gridTemplateRows: 'auto',
                gridAutoRows: 'minmax(22px,1fr)',
            }}
        >
            {weekdayDescriptors.map((day, i) => (
                <span key={i}>{day.abbreviation}</span>
            ))}
            {Array.from({ length: numberOfWeeks }, (_, weekNumber) => (
                <WeekPreview key={weekNumber} assignment={assignment} weekNumber={weekNumber + 1} onWeekdayClick={onWeekdayClick} />
            ))}
        </div>
    </div>
)
